import { useMemo } from 'react';
import { request } from 'graphql-request';
import {
  useBaseLineageGraphQuery,
  type LineageGraphSource,
} from '@elementary/common-ui/components/lineageGraph';
import type { HopLimit } from '@elementary/common-ui/components/lineageGraph/types';
import { INCIDENT_GRAPHQL_LINEAGE_GRAPH } from '@elementary/common-ui/constants/queryKeys';
import useGraphqlRequestParams from '@elementary/common-ui/hooks/useGraphqlRequestParams';
import { getIncidentLineageGraphQuery } from '../api';
import type {
  IncidentLineageGraph,
  IncidentLineageGraphResponse,
} from '../types';

type IncidentLineageGraphQueryResponse = {
  incidentV2: { id: string; lineageGraph: IncidentLineageGraphResponse } | null;
};

const useIncidentLineageGraph = (
  incidentId: string | undefined,
  hopLimit: HopLimit,
  enabled: boolean,
) => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams(
    {},
  );
  const upstream = hopLimit.upstream ?? 0;
  const downstream = hopLimit.downstream ?? 0;

  const source = useMemo(():
    | LineageGraphSource<IncidentLineageGraph>
    | undefined => {
    if (!incidentId || !accessToken || !environmentId) return undefined;
    return {
      queryKey: [
        INCIDENT_GRAPHQL_LINEAGE_GRAPH,
        incidentId,
        environmentId,
        upstream,
        downstream,
      ],
      fetchGraph: async (view) => {
        const data = await request<IncidentLineageGraphQueryResponse>(
          gqlEndpoint,
          getIncidentLineageGraphQuery,
          { id: incidentId, upstream, downstream, view },
          { authorization: `Bearer ${accessToken}` },
        );
        return data.incidentV2
          ? { ...data.incidentV2.lineageGraph, incidentId: data.incidentV2.id }
          : null;
      },
    };
  }, [
    accessToken,
    downstream,
    environmentId,
    gqlEndpoint,
    incidentId,
    upstream,
  ]);

  const { data, isLoading, isFetching, error } = useBaseLineageGraphQuery(
    source,
    { enabled, keepPreviousData: true },
  );

  return {
    source,
    lineageGraph: data && data.incidentId === incidentId ? data : undefined,
    isLoading,
    isFetching,
    error,
  };
};

export default useIncidentLineageGraph;
